import type { Dict } from "./types";

/** Engleza. Textul e cel care era deja în pagini, mutat aici cuvânt cu cuvânt —
 *  traducerea nu e o ocazie de a rescrie originalul pe furiș. */
export const en: Dict = {
  tag: "en",

  nav: { index: "Home", services: "Services", work: "Work", blog: "Blog", faq: "Answers", contact: "Contact", menu: "Menu", closeMenu: "Close menu" },

  workPage: {
    eyebrow: "Work",
    title: "Seven projects, six of them running in production",
    intro: "Every project here shipped or is running today, and each one has its own page with the problem, the approach and what it actually did. One is an open experiment and says so.",
    open: "Open the case",
    task: "Task",
    solution: "Solution",
    result: "Result",
  },

  servicesPage: { eyebrow: "Services", title: "Seven things we build, and what goes into each", intro: "No packages and no tiers. Each of these is a real body of work with its own risks, and the list below is what actually goes into it — not a menu written to look complete." },

  faqPage: { eyebrow: "Answers", title: "What a project costs, how long it takes, and what you own after", intro: "The questions that decide whether you write to us, answered without the parts a proposal usually hides. If yours isn’t here, ask it in the form — we answer either way." },

  contactPage: {
    eyebrow: "Contact",
    title: "Tell us what has to change, and get an answer within 24 hours",
    intro: "The fastest way to start is the form below: two clicks and whatever you want to tell us about the project. If you would rather just write, the address is under it.",
    directHead: "Or write directly",
    directText: "Plain email works as well as the form. Nothing is lost by skipping it.",
    callHead: "Or book the call",
    callText: "If you would rather talk first, take a slot directly. No brief needed beforehand — we work out the scope on the call.",
    callBtn: "Book a call",
    chatHead: "Or message us",
    chatText: "WhatsApp or Telegram, whichever you already have open. Same answer, and a faster start: these reach a person, not a bot and not a shared inbox.",
    whereHead: "Where we are",
    hours: "Monday to Friday, 9:00–18:00",
    nextHead: "What happens after you send",
    next: [
      "A person reads it — no queue, no autoresponder.",
      "You get an answer within 24 hours, including when it isn’t a fit.",
      "If it is a fit, the next step is a free call, not an invoice.",
    ],
  },

  seeAll: { services: "All seven services", faq: "All answers", work: "All work" },

  home: {
    h1: ["Software forged", "for production."],
    sub: "Websites, desktop applications, automations. Shipped end to end, then kept running.",
    ctaStart: "Start a project",
    ctaWork: "See the work",
    scale: [
      { n: "4", l: "years" },
      { n: "20", l: "projects" },
      { n: "6", l: "countries" },
    ],
    painsHead: "Where does it hurt?",
    workHead: "Selected work",
    servicesHead: "Services",
    stepsHead: "How it runs",
    stepsEyebrow: "From the first call to running in production",
    faqHead: "FAQ",
    faqEyebrow: "Everything a buyer asks before the first call",
    briefHead: "Start a project",
    rows: [
      {
        nm: "Multi-warehouse retail ERP",
        st: "Three locations on one stock. Sale to fiscal receipt in one click. · Desktop, 2025",
      },
      {
        nm: "Edge-AI computer vision",
        st: "30 fps on the device, with no cloud round trip. · Embedded, 2026",
      },
      {
        nm: "Bilingual e-commerce",
        st: "1 500 visitors a day, two languages, card payments live. · Web, 2025",
      },
      {
        nm: "AI trading research bot",
        st: "~+1.5% median daily P&L — on paper, still an experiment. · Automation, 2026",
      },
    ],
    services: [
      {
        id: "cap-web",
        title: ["Web &", "e-commerce"],
        p: "Storefronts, portals, dashboards and booking platforms — including the payment and fiscal plumbing behind them.",
        items: ["Storefronts and catalogues", "Portals and dashboards", "Card payments and fiscal receipts", "Multilingual, indexable per language"],
        kind: "Web app",
      },
      {
        title: ["Mobile", "apps"],
        p: "Native iOS and Android where the hardware matters, cross-platform where reach matters — and an honest answer about which one you need.",
        items: ["Native iOS and Android", "Cross-platform from one codebase", "Offline-first data", "Push notifications and deep links"],
        kind: "Mobile app",
      },
      {
        id: "cap-desktop",
        title: ["Desktop &", "embedded"],
        p: "Software for the shop floor: works when the network doesn’t, talks to devices, and runs on hardware we can design if it doesn’t exist yet.",
        items: ["Cross-platform desktop apps", "Offline-first, syncs on reconnect", "Serial, USB and fiscal devices", "Custom PCB when no board fits"],
        kind: "Desktop",
      },
      {
        id: "cap-auto",
        title: ["Automation &", "integrations"],
        p: "The systems you already pay for, made to talk to each other, so nobody retypes anything into a second screen.",
        items: ["ERP, CRM and database integration", "Third-party APIs and webhooks", "Telegram and email notifications", "Scheduled jobs and data pipelines"],
        kind: "Automation",
      },
      {
        title: ["AI that", "ships"],
        p: "Models used where they earn their keep, with ordinary deterministic code holding the boundaries around them.",
        items: ["On-device detection and OCR", "LLM features with human review", "Generated copy, edited before publishing", "Deterministic limits around the model"],
        kind: "Automation",
      },
      {
        title: ["Data &", "dashboards"],
        p: "The numbers that tell you whether the thing works, in one place instead of five exports nobody reconciles.",
        items: ["Data model and schema design", "Reporting dashboards", "Analytics setup, privacy-first", "Migration off spreadsheets"],
        kind: "Automation",
      },
      {
        title: ["Technical", "audit"],
        p: "A read of what you already have, before you decide whether to fix it or replace it. You get the findings whether or not you hire us.",
        items: ["Performance and Core Web Vitals", "Technical SEO and indexability", "Architecture and code review", "Dependency and security check"],
        kind: "Not sure yet",
      },
    ],
    steps: [
      {
        n: "01",
        h: "First call",
        p: "A free call to hear the problem and say whether we are the right fit for it. No deck, no discovery invoice. If it isn’t a fit, you find that out here rather than three weeks in.",
      },
      {
        n: "02",
        h: "Scope",
        p: "User flows, data model, success metrics. We agree what done means before anything is built, and the price is fixed against that — or a retainer if the work is open-ended.",
      },
      {
        n: "03",
        h: "Design",
        p: "Tokens and components once, reused everywhere. No templates and no packages: your product doesn’t come out looking like the last one we shipped.",
      },
      {
        n: "04",
        h: "Build",
        p: "Frontend, backend, data tier, integrations. You see progress every week and approve each stage before the next one starts, so nothing lands as a surprise at the end.",
      },
      {
        n: "05",
        h: "Operate",
        p: "CI/CD, monitoring, runbook. We don’t disappear at launch — and you aren’t locked in either: code, docs and runbook are yours to run without us.",
      },
    ],
  },

  pains: {
    items: [
      "I need a site that brings work, not compliments",
      "My team retypes the same data all day",
      "My tools don’t talk to each other",
      "The shop floor needs software that works offline",
      "We have hardware and nothing to run on it",
      "We know what hurts, not what to build",
    ],
    outHead: "Not on the list?",
    outText: "Describe it in your own words. We answer either way, including when it isn’t a fit.",
    outBtn: "Describe the task",
    defects: [
      { n: "Porosity", leg: "Sound on the outside, hollow inside. It looks finished and carries nothing." },
      { n: "Wear channel", leg: "The same pass, every day, cutting a groove nobody budgeted for." },
      { n: "Cold shut", leg: "Two flows met and never welded. The seam holds until it is loaded." },
      { n: "Heat-affected zone", leg: "Next to the process the metal is not the metal in the catalogue." },
      { n: "Unfilled cavity", leg: "The cavity is machined. Nothing ever flowed into it." },
      { n: "Dimension, no value", leg: "The measurement is set up. The number is the thing we find." },
    ],
  },

  brief: {
    steps: [
      { q: "What are we building?", opts: ["Web app", "Mobile app", "Desktop", "Automation", "Not sure yet"] },
      { q: "When does it start?", opts: ["ASAP", "1–3 months", "Flexible"] },
    ],
    detailsQ: "Tell me about it",
    detailsPlaceholder: "What it has to do, what exists today, and anything it has to talk to. Rough is fine — I ask the rest on the call.",
    detailsOptional: "Optional, but the more you write the more useful my first answer is.",
    mailQ: "Where do I send the scope?",
    mailPlaceholder: "your@email.com",
    send: "Send brief",
    sending: "Sending…",
    sendByEmail: "Send by email",
    botFailed:
      "The bot check can’t run in this browser, so the form can’t submit. The button above opens an email with your answers already filled in.",
    badEmail: "That email doesn’t look right.",
    generic: "Something went wrong.",
    doneText: "Answer within 24 hours.",
    another: "Send another",
    block: "Title block",
  },

  serviceBack: "← All services",
  stackNote: "A link beside a group means it is running in production in a project you can read about. The rest is what the team also covers.",
  stackProof: "In production:",
  stackAlso: "Also covered",
  serviceOther: "Other services",
  serviceMore: "Read about this service",
  servicePages: [
    {
      slug: "web", num: "01", eyebrow: "Web & e-commerce", kind: "Web app",
      title: "Storefronts, portals and the payment plumbing behind them",
      intro:
        "A web project is rarely just pages. It is a catalogue that has to stay correct, a payment that has to clear through a specific bank, a receipt that has to satisfy a tax authority, and a second language that has to be complete rather than half-translated. We build the whole of that, not the part that photographs well.",
      includedHead: "What is included",
      included: [
        { h: "Storefronts and catalogues", p: "Product data as data, not as pages. Adding an item is a database row, so the catalogue does not rot as it grows, and search and filters keep working at a thousand products." },
        { h: "Portals and dashboards", p: "The screens your staff live in all day. Built for speed of entry rather than for a screenshot: keyboard first, no modal that hides the row you were reading." },
        { h: "Card payments and fiscal receipts", p: "Local payment rails, not only Stripe. We have shipped MAIB card payments with fiscal invoicing on order completion, which is the part most projects discover too late." },
        { h: "Multilingual, indexable per language", p: "Each language gets its own address and its own meta, so both versions can rank separately. Content lives per locale at the field level, so a page can be complete in one and pending in another without breaking." },
        { h: "Prerendered for crawlers", p: "Every route is rendered to static HTML at build, so a search engine or a social scraper gets a full page instead of an empty shell that fills in after JavaScript." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "Online shops and catalogues", p: "Stock, variants, orders and a checkout that clears through the bank your customers actually use, with the receipt the tax authority expects." },
        { h: "Booking and reservation platforms", p: "Availability, pricing rules, confirmations and the dispatcher's calendar — with the notification reaching a phone rather than an inbox nobody opens." },
        { h: "Client portals and internal panels", p: "The screens staff work in all day: orders, documents, statuses, roles, and who is allowed to change what." },
        { h: "Marketplaces with several sellers", p: "Seller accounts, commissions, payouts and moderation — the part that turns a catalogue into a place other people can sell in." },
        { h: "Corporate sites in several languages", p: "Each language a real version with its own address and meta, not a plugin translating over the top of a single one." },
        { h: "CMS and headless publishing", p: "WordPress or a headless CMS behind a fast front end, so marketing publishes without waiting for a developer." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "Front end", items: ["React", "TypeScript", "Vite", "Tailwind", "shadcn/ui", "i18next", "react-hook-form", "Sass"], proof: "createli", also: ["Next.js", "Vue", "Nuxt", "Astro"] },
        { h: "CMS and PHP", items: ["WordPress", "WooCommerce", "PHP", "Laravel"] },
        { h: "Back end and data", items: ["Node.js", "Express", "PostgreSQL", "MySQL", "Sequelize", "REST APIs"], proof: "commerce", also: ["Python", "FastAPI", "Django", ".NET", "Java", "Spring", "MongoDB", "Redis", "ClickHouse"] },
        { h: "Payments and fiscal receipts", items: ["MAIB", "fiscal invoicing"], proof: "commerce", also: ["MICB", "PayNet", "Stripe", "PayPal"] },
        { h: "Hosting and indexing", items: ["nginx", "PM2", "Puppeteer prerender", "JSON-LD", "sitemaps", "hreflang"], proof: "elegance", also: ["AWS", "Google Cloud", "Azure", "Docker", "Kubernetes", "Vercel", "Netlify", "Fly.io"] },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Shops selling in several languages", p: "If your customers do not all read the same language, a translation plugin will cost you every market it touches. A complete version per language costs less than one broken one stretched across all of them." },
        { h: "Businesses with a local payment problem", p: "Card payments through a regional bank, fiscal receipts, invoicing rules that no international platform implements. This is ordinary work here and exotic everywhere else." },
        { h: "Anyone whose site is invisible to search", p: "If your pages are a JavaScript app, a crawler may be receiving nothing at all. That is checkable in a minute and fixable in a day." },
      ],
    },
    {
      slug: "mobile", num: "02", eyebrow: "Mobile apps", kind: "Mobile app",
      title: "Apps for the phone in your customer's hand, and in your driver's",
      intro:
        "An app earns its cost when the phone does something a browser tab cannot: keep working with no signal, read a barcode, hold a driver's session all day, or send a notification that actually arrives. We build native where the hardware matters and cross-platform where reach matters — and we tell you which one your case is before you pay for either.",
      includedHead: "What is included",
      included: [
        { h: "Native iOS and Android", p: "Swift on iOS, Kotlin on Android. The right route when the app leans on the device: camera, background location, Bluetooth, secure storage, or performance a wrapper will not give you." },
        { h: "Cross-platform from one codebase", p: "React Native or Flutter when the app is mostly screens and data. Both stores from one codebase halves the cost of every change that comes after launch." },
        { h: "Offline-first data", p: "The app owns its data locally and reconciles when the network returns. A courier in a basement and a warehouse with no coverage are the normal case, not the edge case." },
        { h: "Push notifications and deep links", p: "A notification that opens the right screen rather than the home screen — including the unglamorous half: tokens, silent delivery failures, and what happens after a reinstall." },
        { h: "Store submission and updates", p: "Review rules, privacy declarations, signing and the update channel afterwards. Published under your developer account, not ours, so the app stays yours." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "Companion apps for a system you already run", p: "The phone half of a shop, an ERP or a booking platform, talking to the same database the web app already uses." },
        { h: "Field and courier apps", p: "Routes, scans, signatures and photos captured offline and uploaded when a signal comes back." },
        { h: "Ordering and booking apps", p: "Catalogue, cart, payment and order status, with notifications that follow the order instead of an inbox." },
        { h: "Loyalty and customer accounts", p: "Cards, points, history and offers — the reason an app is still installed a month after the first order." },
        { h: "Apps that talk to hardware", p: "Bluetooth scales, printers, scanners and terminals. The category where native is a requirement rather than a preference." },
        { h: "Installable web apps", p: "When a store listing adds cost without adding reach: installable straight from the browser, no review queue, no thirty per cent." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "Native", items: ["Swift", "SwiftUI", "Kotlin", "Jetpack Compose", "Xcode", "Android Studio"] },
        { h: "Cross-platform", items: ["React Native", "Expo", "Flutter", "Dart", "TypeScript"] },
        { h: "Installable web", items: ["React", "service workers", "Web Push", "offline cache", "Web App Manifest"] },
        { h: "The back end an app talks to", items: ["Node.js", "Express", "PostgreSQL", "MySQL", "REST APIs", "token sessions"], proof: "commerce", also: ["Python", "FastAPI", "Django", "Redis", "WebSocket"] },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Businesses whose customers are already on a phone", p: "If most of your traffic is mobile and the checkout is where people drop out, an app is worth costing before another redesign." },
        { h: "Teams working away from a desk", p: "Couriers, technicians, drivers, inspectors. People holding a phone in one hand, who need three taps rather than a form." },
        { h: "Anyone told they need both platforms", p: "Sometimes they do. Often one platform plus an installable web app reaches the same audience for half the money, and that is worth an hour of arithmetic first." },
      ],
    },
    {
      slug: "desktop", num: "03", eyebrow: "Desktop & embedded", kind: "Desktop",
      title: "Software for the shop floor, and the hardware under it",
      intro:
        "Some work does not belong in a browser tab. A till that has to keep selling when the internet drops, a machine that talks over a serial port, a board that does not exist yet. We write that software, and when no board fits we design the board.",
      includedHead: "What is included",
      included: [
        { h: "Cross-platform desktop apps", p: "One codebase for Windows, macOS and Linux, installed and updated like a real application rather than a web page pretending to be one." },
        { h: "Offline-first, syncs on reconnect", p: "The app owns its data locally and reconciles when the network returns. A shop does not stop selling because an uplink flickered." },
        { h: "Serial, USB and fiscal devices", p: "Fiscal registers, scanners, scales, printers. We have driven a TREMOL S fiscal register from an Electron app, which means the protocol work is done rather than theoretical." },
        { h: "Custom PCB when no board fits", p: "Schematic and layout in KiCad, then the firmware and the vision or control pipeline that runs on it. One team across the whole stack means the hardware and the software are designed against each other." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "Point of sale and back office", p: "Selling, stock, shifts and fiscal receipts on the same machine, still working when the uplink drops mid-transaction." },
        { h: "Warehouse and production terminals", p: "Scanning, weighing, labelling and moving stock, on hardware that has to survive a warehouse rather than a desk." },
        { h: "Machine and device control", p: "Serial, USB, Modbus and vendor protocols, behind a screen an operator can use without a manual." },
        { h: "Edge AI devices", p: "Detection, OCR or classification running on the board itself — no round trip to a data centre, no per-call bill, no outage when the line drops." },
        { h: "Custom boards, schematic to production", p: "Schematic and layout, prototype, firmware, then the production run, for when no off-the-shelf board is the right shape or the right price." },
        { h: "IoT and sensor networks", p: "Microcontrollers reading sensors and reporting home, with the server side and the dashboard that make the readings mean something." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "Desktop", items: ["Electron", "React", "TypeScript", "Zustand", "TanStack Query", "MUI", "Express", "Sequelize", "PostgreSQL"], proof: "erp", also: ["Tauri", "Qt", "C#", ".NET", "WPF", "Python"] },
        { h: "Devices and protocols", items: ["serialport", "USB HID", "TREMOL S fiscal register", "barcode scanners", "scales", "receipt printers"], proof: "erp", also: ["Modbus", "RS-485", "CAN", "MQTT"] },
        { h: "Embedded and edge", items: ["C++17", "RK3576", "RKNN", "GStreamer", "systemd", "LPDDR4X"], proof: "vision", also: ["ESP32", "STM32", "Raspberry Pi", "NVIDIA Jetson", "FreeRTOS", "Zephyr", "Yocto", "Buildroot"] },
        { h: "Hardware design", items: ["KiCad", "schematic capture", "PCB layout"], proof: "vision", also: ["prototype runs", "assembly", "enclosure design"] },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Retail and hospitality", p: "Point of sale, stock, fiscal compliance. The category where an outage during business hours is not an inconvenience but lost revenue." },
        { h: "Manufacturing and logistics", p: "Anything that reads a device, a sensor or a scale and has to record it correctly the first time, because nobody will re-enter it later." },
        { h: "Products that need their own hardware", p: "When an off-the-shelf board is the wrong shape, the wrong price or missing an interface, designing one is often cheaper than working around it forever." },
      ],
    },
    {
      slug: "automation", num: "04", eyebrow: "Automation & integrations", kind: "Automation",
      title: "Making the systems you already pay for talk to each other",
      intro:
        "Most companies do not need another tool. They need the four they already have to stop being islands, so that nobody retypes an order from one screen into another and nobody reconciles two spreadsheets that should have been one.",
      includedHead: "What is included",
      included: [
        { h: "ERP, CRM and database integration", p: "One source of truth, and the rest reading from it. Usually this means picking which system wins on each field, which is a business decision before it is a technical one." },
        { h: "Third-party APIs and webhooks", p: "Payment providers, couriers, marketplaces, accounting. Including the unglamorous parts: retries, idempotency, and what happens when the other side is down." },
        { h: "Telegram and email notifications", p: "The event reaches the person where they already work. A dispatcher who lives in Telegram should not have to open a dashboard to learn that a booking arrived." },
        { h: "Scheduled jobs and data pipelines", p: "Imports, exports, reconciliations and reports that run on their own and tell you when they fail — rather than failing silently until somebody notices a number is wrong." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "System-to-system integrations", p: "Shop to ERP, ERP to accounting, CRM to warehouse: one source of truth and the rest reading from it." },
        { h: "Chat bots that do real work", p: "Telegram, WhatsApp or Viber bots that take an order, answer a status question or alert a dispatcher, wired to the actual database rather than a script of canned replies." },
        { h: "Document and report generation", p: "Invoices, waybills, contracts and monthly reports produced from data instead of retyped out of it." },
        { h: "Scheduled jobs and reconciliations", p: "Imports, exports and nightly checks that run on their own and tell someone when they fail, instead of failing quietly for a month." },
        { h: "Data collection from sources without an API", p: "Prices, stock or listings read from pages when the other side offers no other way in — on a schedule and within the limits that source sets." },
        { h: "Robots on legacy interfaces", p: "When a system has no API at all, a script that drives its interface the way a person would. The last resort, and often the only one." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "Runtime and scheduling", items: ["Node.js", "Express", "Python", "cron", "systemd timers", "background workers"], proof: "commerce", also: ["BullMQ", "Celery", "Redis", "RabbitMQ"] },
        { h: "Channels", items: ["Telegram Bot API", "Mailgun", "Resend", "SMTP"], proof: "elegance", also: ["WhatsApp Business API", "Viber", "SMS gateways"] },
        { h: "Business systems", items: ["in-house databases", "REST and webhook APIs", "CSV and XML exchange"], proof: "erp", also: ["1C", "Bitrix24", "amoCRM", "HubSpot", "Salesforce"] },
        { h: "Low-code platforms", items: ["n8n", "Make", "Zapier"] },
        { h: "Browser automation", items: ["Playwright", "Puppeteer"], proof: "elegance", also: ["scheduled collection", "legacy UI robots"] },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Teams retyping the same data twice", p: "The clearest signal that an integration will pay for itself: somebody's job contains the phrase “copy it into”." },
        { h: "Businesses outgrowing spreadsheets", p: "Spreadsheets are excellent until two people edit them. The migration is usually less work than the year of workarounds that precedes it." },
        { h: "Anyone whose tools do not have an official connector", p: "Regional systems, older ERPs, in-house databases. No marketplace integration exists, so it gets written." },
      ],
    },
    {
      slug: "ai", num: "05", eyebrow: "AI that ships", kind: "Automation",
      title: "Models used where they earn their keep, with limits around them",
      intro:
        "AI is a component, not a product. It is worth using where the alternative is a human doing something repetitive, and worth avoiding where a deterministic rule is cheaper and never wrong. The interesting engineering is in the boundary between the two.",
      includedHead: "What is included",
      included: [
        { h: "On-device detection and OCR", p: "Inference that runs on the machine rather than in a data centre: no round trip, no per-call cost, and it keeps working when the connection does not. We have shipped detection and OCR under 100 ms on a custom board." },
        { h: "LLM features with human review", p: "The model proposes, a person approves, and the approval is part of the workflow rather than a policy nobody follows. Generated text that nobody reads is worse than no text." },
        { h: "Generated content, edited before publishing", p: "Product descriptions and copy drafted from the spec sheet, then edited. Hundreds of items become tractable without the catalogue reading like a machine wrote it, because a person still did." },
        { h: "Deterministic limits around the model", p: "Ordinary code holds the boundaries: what the model may spend, what it may change, and what a human must confirm. The model is never the last checkpoint before something irreversible." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "On-device detection and recognition", p: "Counting, sorting, or reading labels and plates on the machine itself, at video frame rate and with no cloud bill attached." },
        { h: "Document and receipt OCR", p: "Scans and photos turned into fields a system can use, with the uncertain ones routed to a person rather than guessed at." },
        { h: "Text drafted from your own data", p: "Product descriptions, summaries and replies generated from the spec sheet or the record, then edited by a person before anyone outside sees them." },
        { h: "Classification and routing", p: "Tickets, messages and documents sorted into the right queue automatically, with the low-confidence ones handed to a human." },
        { h: "Research and decision support", p: "A model reading the same inputs a person would and proposing an action, with ordinary code deciding what it is allowed to do with it." },
        { h: "Private, self-hosted models", p: "Llama, Mistral or similar running on your own server, for when the data cannot leave the building or the per-call price does not work." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "On-device vision", items: ["YOLO", "ONNX Runtime", "RKNN", "OpenCV DNN", "PP-OCRv4", "C++17"], proof: "vision", also: ["NVIDIA Jetson", "TensorRT", "Tesseract"] },
        { h: "Commercial model APIs", items: ["OpenAI", "Anthropic Claude"], proof: "trading" },
        { h: "Self-hosted models", items: ["Llama", "Mistral", "Ollama"] },
        { h: "Around the model", items: ["Python 3.12", "SQLAlchemy", "APScheduler", "risk engine", "human review step"], proof: "trading" },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Catalogues too large to write by hand", p: "Hundreds of products with no descriptions is a real cost. It is also the case where drafting plus editing beats both writing from scratch and publishing raw output." },
        { h: "Products that must work without a network", p: "Retail floors, vehicles, industrial sites. If the cloud is optional, inference has to happen locally, which changes the model you can use and how it is built." },
        { h: "Anyone unsure whether AI belongs here at all", p: "Sometimes the honest answer is that a rule would do it better and cheaper. We would rather say that before you commit than after." },
      ],
    },
    {
      slug: "data", num: "06", eyebrow: "Data & dashboards", kind: "Automation",
      title: "The numbers that say whether the thing works, in one place",
      intro:
        "Most businesses are not short of data. They are short of one version of it. Five exports that nobody reconciles is not reporting, it is an argument waiting to happen every month.",
      includedHead: "What is included",
      included: [
        { h: "Data model and schema design", p: "The part that decides whether every later question is easy or expensive. Done once, carefully, before it hardens into something nobody dares change." },
        { h: "Reporting dashboards", p: "The handful of numbers a decision actually turns on, updated automatically. Not a wall of charts that everyone stops opening after the second week." },
        { h: "Analytics setup, privacy-first", p: "Measurement that does not require a cookie banner the size of the page. You get the numbers you need and your visitors keep their privacy." },
        { h: "Migration off spreadsheets", p: "Moving the history without losing it, including the special cases hidden in colour codes and comments that no export will carry." },
      ],
      buildsHead: "What we build",
      builds: [
        { h: "One dashboard for the business", p: "Revenue, stock, orders and margin in one place, updated on its own, instead of five exports nobody reconciles." },
        { h: "Operational screens for a team", p: "What a warehouse, a dispatcher or a support desk keeps open all day, showing what needs attention right now rather than last month." },
        { h: "Reports that send themselves", p: "The weekly or monthly numbers arriving in an inbox or a chat, in the same shape every time, without anyone assembling them." },
        { h: "Schema and data model design", p: "The decision that makes every later question cheap or expensive, made once and deliberately instead of by accident." },
        { h: "Migration off spreadsheets", p: "Moving the history without losing the meaning hidden in colour codes, comments and one person's memory." },
        { h: "Privacy-first web analytics", p: "Traffic and conversion numbers without a consent banner the size of the page, and without handing your visitors to an ad network." },
      ],
      stackHead: "Stack",
      stack: [
        { h: "Dashboards inside your own product", items: ["React", "TypeScript", "Recharts", "TanStack Query", "server-side aggregation"], proof: "erp", also: ["D3", "virtualised tables"] },
        { h: "Ready-made BI, self-hosted", items: ["Metabase", "Grafana", "Superset"] },
        { h: "Storage and queries", items: ["PostgreSQL", "MySQL", "SQLite", "indexing and query tuning"], proof: "erp", also: ["ClickHouse", "MongoDB", "Redis"] },
        { h: "Analytics", items: ["Umami", "server-side events", "Search Console data"], also: ["Plausible", "Matomo"] },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Companies reporting from memory", p: "If the monthly number depends on who assembled it, the number is an opinion. Making it reproducible is usually a week of work." },
        { h: "Teams with data in four places", p: "Shop, accounting, courier, spreadsheet. Each is right about its own part and none of them agree on the total." },
        { h: "Anyone who does not know what to measure", p: "Starting with the decision rather than the chart. Two numbers you act on beat forty you look at." },
      ],
    },
    {
      slug: "audit", num: "07", eyebrow: "Technical audit", kind: "Not sure yet",
      title: "A read of what you have, before you decide to fix or replace it",
      intro:
        "Rewriting is expensive and often unnecessary. Before you decide, it helps to know what is actually wrong: whether the code is the problem, whether the architecture is, or whether one bad decision is doing all the damage. You get the findings whether or not you hire us afterwards.",
      includedHead: "What is included",
      included: [
        { h: "Performance and Core Web Vitals", p: "Measured on your live site, not simulated. Largest paint, layout shift, page weight, request count — and which of them a visitor actually notices." },
        { h: "Technical SEO and indexability", p: "Whether a crawler receives your pages at all. This is where we most often find something serious: a JavaScript app serving an empty document to every search engine." },
        { h: "Architecture and code review", p: "Where the structure will fight you as the product grows, and where it is fine. Including which parts are worth keeping — usually more than people expect." },
        { h: "Dependency and security check", p: "Known vulnerabilities, abandoned packages, secrets in the wrong places, and the configuration mistakes that only show up under load or under attack." },
      ],
      buildsHead: "What we look at",
      builds: [
        { h: "Speed on the devices your customers use", p: "Core Web Vitals measured on your live site and on real phones, not on a simulator with a fibre connection." },
        { h: "Whether search engines see you at all", p: "The most common serious finding: a JavaScript app serving an empty document to every crawler — including the ones behind AI answers." },
        { h: "Security posture", p: "OWASP checks, vulnerable and abandoned dependencies, secrets in the wrong places, and server configuration that only fails under attack." },
        { h: "Load and breaking point", p: "How many users it holds before it falls over. Worth answering before a campaign rather than during one." },
        { h: "Database and query health", p: "Slow queries, missing indexes, and the table that will stop the site at four times today's traffic." },
        { h: "Cloud bill and monitoring", p: "What you are paying for and whether it is used, and whether anything would tell you if it broke at three in the morning." },
        { h: "Architecture and code", p: "Where the structure will fight you as the product grows, and — usually more than expected — which parts are worth keeping." },
      ],
      stackHead: "What we use",
      stack: [
        { h: "Performance", items: ["Lighthouse", "Core Web Vitals", "WebPageTest", "bundle analysis", "Chrome DevTools"], proof: "elegance" },
        { h: "Search and indexability", items: ["Search Console", "Bing Webmaster Tools", "crawl simulation", "JSON-LD validation", "sitemap and hreflang checks"], proof: "actexpress" },
        { h: "Security", items: ["OWASP Top 10", "dependency audit", "secret scanning", "TLS and header review"] },
        { h: "Load testing", items: ["k6", "Locust"] },
        { h: "Infrastructure and observability", items: ["query plans and indexes", "nginx and PM2 review", "Sentry", "uptime and alerting", "cloud cost review"], proof: "erp" },
      ],
      fitsHead: "Who it fits",
      fits: [
        { h: "Before a rewrite decision", p: "The cheapest hour you will spend on the question. Sometimes the answer is that the rewrite is right, and then you have evidence rather than a feeling." },
        { h: "After inheriting a system", p: "A new developer, a new CTO, an acquisition. You need to know what you now own before you promise anyone a date." },
        { h: "When traffic is not converting", p: "Sometimes the marketing is fine and the site is quietly broken — slow on the phones your customers use, or invisible to search." },
      ],
    },
  ],
  industriesPage: {
    eyebrow: "Solutions",
    title: "Every trade and every city we have written a page for",
    intro: "Fifty-four situations, each with its own page: what the work looks like in that trade, what it costs there, and which service it ends up being. Start from the one that matches you rather than from a list of technologies.",
    short: "Solutions",
    geo: "By place",
    vertical: "By trade",
    geoService: "A service in a place",
    forService: "Situations we have written up",
  },

  niche: {
    eyebrow: "Solutions",
    back: "All solutions",
    serviceHead: "The service behind it",
    nearHead: "Nearby situations",
    ctaHead: "Tell us what you have and what it has to do. We answer with what is realistic, not a template quote.",
  },

  blog: {
    eyebrow: "Blog",
    title: "What we learned building things that had to work",
    intro: "Engineering notes from projects in production, and answers to the questions clients ask before they commit. Written when there is something worth writing, not on a schedule.",
    listHead: "All articles",
    minutes: "min read",
    published: "Published",
    updated: "Updated",
    toc: "On this page",
    back: "← All articles",
    serviceHead: "The service this is about",
    caseHead: "Where this came from",
    nextHead: "Next",
    empty: "The first articles are being written. Come back shortly, or ask the question directly — the answer arrives faster that way.",
  },
  tool: {
    eyebrow: "Open source",
    title: "crawlview",
    tagline: "See what search engines and AI crawlers actually store for your pages — not what your browser shows you.",
    intro: [
      "Your browser runs JavaScript, so the page in DevTools is a finished page. Most crawlers do not run it. What they keep can be an empty container, and nothing on screen tells you.",
      "crawlview requests a page once as each crawler, compares what comes back, and says where the browser and the machines disagree. One command, no account, nothing to install.",
    ],
    runLbl: "Run it",
    runHead: "One command",
    runNote: "Needs Node 20 or newer. The browser comparison uses a Chrome already on the machine — nothing is downloaded behind your back.",
    outputLbl: "What it looks like",
    outputHead: "A page that renders in the browser and nowhere else",
    outputNote: "The table is the report. The sentence under it is the diagnosis — which is the part a crawl of raw HTML cannot give you.",
    checksLbl: "What it checks",
    checksHead: "Nine groups of checks",
    checks: [
      { h: "Divergence", p: "What each crawler stores against what a browser renders: text, structured data, and internal links that only exist after JavaScript runs — which is how a whole site becomes undiscoverable from its own homepage." },
      { h: "robots.txt, per crawler", p: "Whether each crawler is allowed this exact URL and which rule decided it, including Google-Extended and Applebot-Extended: tokens that gate AI answers but never fetch anything, so no other method can observe them." },
      { h: "Indexing directives", p: "X-Robots-Tag headers, which are invisible in the page source and outrank the meta tag. A stray noindex in a header survives for months because nobody thinks to look there." },
      { h: "Structured data", p: "Required properties, and claims the page itself never makes. Markup may only describe what a visitor can see; a price or phone number that exists solely in the JSON-LD is what manual actions are issued for." },
      { h: "Declared language against real language", p: "A translated route that renders the default copy declares one language and serves another. The tell is identical word counts across translations, and the site looks perfect until somebody reads it." },
      { h: "hreflang", p: "Every alternate is fetched and checked for a link back. A set where one member does not reciprocate is discarded whole — the other translations lose their links too." },
      { h: "AI answer readiness", p: "Whether crawlers that never run JavaScript receive anything, how much text survives content extraction, whether headings produce usable sections, and whether the page can be cited at all." },
      { h: "Site-wide", p: "Sitemap entries that 404, redirect, are noindex or disallowed; pages that canonicalise elsewhere; orphans linked from nowhere; duplicate titles; routes that are secretly the same page." },
      { h: "Soft 404s", p: "A URL that cannot exist answering 200 — what a single-page app does to every misspelled link on the internet that points at your site." },
    ],
    ciLbl: "In a pipeline",
    ciHead: "Fail the build, not the quarter",
    ci: [
      "A rendering regression is usually found weeks later, in Search Console, after the traffic has already gone. It is a build failure that nobody arranged to catch.",
      "crawlview exits non-zero when a crawler sees less than a set share of the browser's text, and a committed snapshot turns that into a comparison against the last known-good run. A published GitHub Action wires it into a pull request.",
    ],
    linksLbl: "Where it lives",
    linksHead: "Source and package",
    repo: "Source on GitHub",
    npm: "Package on npm",
    ctaHead: "Ran it on your site and want the problems gone?",
    ctaBtn: "Tell us what it found",
  },
  about: {
    eyebrow: "A team, four years, twenty projects",
    title: "About",
    intro: [
      "coresmith.dev is an independent software engineering studio. Design, code, deployment and the part afterwards where it has to keep running — all of it in-house, by the same team. There is no account manager standing between you and the people building the software, and nothing is subcontracted without telling you.",
      "The team is deliberately small and the work is deliberately narrow. That is a real constraint rather than a positioning statement — we take fewer projects at once, so a start date is a genuine limit and not a negotiating position.",
      "The studio is in Chișinău. Most of the work is for companies in Moldova and Romania; the rest is spread across four other countries.",
    ],
    approachLbl: "How this works",
    approachHead: "Scope first, price second, code third",
    approach: [
      "Most projects go wrong before anyone writes a line, in the gap between what was asked for and what was needed. So the first conversation is about what has to change in your business, not about which framework to use. That conversation is free and usually takes one call.",
      "Then you get a fixed price for an agreed scope, or a monthly retainer if the work is genuinely open-ended. No price list, because a price quoted before scope is a guess dressed as a number.",
      "The work is delivered end to end: design, build, deploy, monitor. At handoff the code is in your repository under your account, with documentation and a runbook, and you are not locked into us to keep your own software alive.",
    ],
    valuesLbl: "What this is built on",
    valuesHead: "Four things we will not trade",
    values: [
      { h: "It has to run in production", p: "Three of the four systems on this site are running in production today. The fourth is an open experiment and says so on its own page. A demo that impresses and never ships is not work, it is a portfolio piece." },
      { h: "The numbers are measured", p: "Every figure on this site was measured rather than estimated: load times from a real browser against the live sites, page weights from the network, route counts from the sitemaps. If we cannot measure it, we do not claim it." },
      { h: "You own what you paid for", p: "Code in your repository, documentation you can hand to someone else, a runbook for the boring parts. The test is simple: could another engineer take this over without calling us. If not, it is not finished." },
      { h: "The honest answer, early", p: "If the thing you asked for is the wrong thing, you hear it before you commit rather than three months in. Sometimes the honest answer is that you do not need a project at all." },
    ],
    worksLbl: "How we work",
    worksHead: "Six habits that decide the outcome",
    works: [
      { h: "Understand the task before choosing the tool", p: "Web app, desktop app, integration — those are already answers. The question they answer has to be asked first, or you get a well-built solution to the wrong problem." },
      { h: "Fewer parts, working correctly", p: "Every dependency, every service and every screen is something that can break at three in the morning. The smallest system that does the job is usually the one still standing in two years." },
      { h: "Measure instead of arguing", p: "Disagreements about performance, structure or priority get settled with a measurement. It takes an hour and ends the discussion permanently." },
      { h: "Weekly demos you can click", p: "Not status reports. Something running that you can open and try, every week, so a wrong direction costs a week rather than a quarter." },
      { h: "Written scope, in plain language", p: "What it does, what counts as done, and explicitly what is not included. The last part prevents more disputes than the first two combined." },
      { h: "Delivery includes the day after", p: "CI/CD, monitoring and the runbook are part of the work, not an upsell. A system nobody can deploy or debug was not delivered, it was abandoned in place." },
    ],
    numbersLbl: "Where this stands",
    numbersHead: "The numbers, plainly",
    numbers: [
      { v: "4", l: "years" },
      { v: "20", l: "projects" },
      { v: "6", l: "countries" },
    ],
    ctaHead: "Start with the scope conversation. It costs nothing and it is usually one call.",
    ctaBtn: "Start a project",
  },
  caseBack: "← All work",
  caseVisit: "Open the site",
  caseCta: "Need something like this?",
  caseCtaBtn: "Start a project",
  caseNext: "Next case",
  caseCount: (i, total) => `Case ${String(i).padStart(2, "0")} / ${String(total).padStart(2, "0")}`,

  cases: [
    {
      scene: "erp",
      slug: "erp",
      index: 1,
      phases: { steps: ["exploded", "placed", "assembled", "synced"], hint: "scroll to assemble the system" },
      title: "Multi-warehouse retail ERP + POS",
      meta: [{ b: "Retail operations" }, { t: "2 months" }, { t: "in-house" }, { b: "shipped" }],
      shotAlt:
        "A render of a point-of-sale station: a monitor showing an open receipt — HDMI cable, USB-C adapter, SSD, mouse, hub and monitor stand — with subtotal, VAT, discount and a total of 4,673 lei, plus the payment button. Beside it, the card terminal and the fiscal printer, joined by cables.",
      blocks: [
        {
          lbl: "The problem",
          h3: "Three locations, one spreadsheet, no fiscal link.",
          p: [
            "Stock lived in spreadsheets that were copied between locations by hand. The point of sale did not know what the warehouse held, and nothing talked to the fiscal printer, so every receipt was re-typed on a separate device.",
            "Counting errors compounded: a sale in one shop could not see a reservation in another.",
          ],
        },
        {
          lbl: "The approach",
          h3: "One schema, one binary, offline-first.",
          p: [
            "A single PostgreSQL schema as the source of truth for stock, prices and documents, with the desktop client talking to it over a thin Express layer. The client runs on the shop floor and keeps working when the network drops.",
            "The fiscal printer was the hard part: driven over serial, from the same action that closes the sale, so a receipt can never diverge from the recorded transaction.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Desktop client on a real database.",
          p: [
            "Electron shell, React 19 with TypeScript, Zustand for local state and TanStack Query for the server cache. Express and Sequelize behind it, PostgreSQL with 43 tables covering locations, movements, documents and audit.",
          ],
        },
        {
          lbl: "The outcome",
          h3: "One click from sale to bon fiscal.",
          p: [],
        },
      ],
      chips: ["Electron", "React 19", "TypeScript", "Zustand", "TanStack Query", "MUI", "Express", "Sequelize", "PostgreSQL", "serialport", "TREMOL S"],
      kpis: [{ v: "43", l: "tables" }, { v: "3", l: "locations" }, { v: "2 mo", l: "to production" }],
      outcome:
        "Stock is one number again. The fiscal receipt prints from the same transaction that records the sale, so the shop and the tax record cannot drift apart.",
    },
    {
      scene: "vision",
      slug: "vision",
      index: 2,
      phases: { steps: ["exploded", "placed", "assembled", "enclosed"], hint: "scroll to assemble the board" },
      title: "Edge-AI computer vision product",
      meta: [{ b: "Real-time CV", t: " · custom hardware" }, { t: "ongoing" }, { t: "in-house" }, { b: "in production" }],
      blocks: [
        {
          lbl: "The problem",
          h3: "Detection and OCR under 100 ms, on a board that did not exist yet.",
          p: [
            "The product needed object detection plus text recognition running on device, fast enough to feel instant, on hardware cheap enough to ship in volume. No cloud round trip.",
            "And one more constraint: the same C++ binary had to run on a developer desktop and on the embedded board, or iteration speed would collapse.",
          ],
        },
        {
          lbl: "The approach",
          h3: "One pipeline, three inference backends.",
          p: [
            "The vision pipeline is written once against an inference interface with three implementations: ONNX Runtime for the desktop, RKNN for the NPU on device, OpenCV DNN as the fallback. Swapping backends is a config change, not a rewrite.",
            "Frames never leave GPU memory between capture and inference: GStreamer moves them zero-copy, which is where most of the latency budget was won.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Software and the board it runs on.",
          p: [
            "C++17, YOLO for detection, PP-OCRv4 (DBNet for text detection, CRNN for recognition) for reading. The device presents itself as a USB HID gadget so the host needs no driver. systemd supervises it.",
            "The board is ours too: an 8-layer PCB around an RK3576 with LPDDR4X, laid out in KiCad.",
          ],
        },
        { lbl: "The outcome", h3: "30 fps, on device, no cloud.", p: [] },
      ],
      chips: ["C++17", "YOLO", "ONNX Runtime", "RKNN", "OpenCV DNN", "PP-OCRv4", "GStreamer", "USB HID", "systemd", "RK3576", "LPDDR4X", "KiCad"],
      kpis: [{ v: "30 fps", l: "pipeline" }, { v: "3", l: "inference backends" }],
      outcome:
        "Detection and OCR run entirely on the board. The same binary debugs on a laptop, which is why the thing shipped at all.",
    },
    {
      scene: null,
      site: { host: "neplaceshop.md", url: "https://neplaceshop.md", w: 1440, h: 4246 },
      slug: "commerce",
      index: 3,
      phases: { steps: [], hint: "" },
      title: "Bilingual electronics e-commerce",
      meta: [{ b: "Website", t: " · online store" }, { t: "2 languages" }, { t: "in-house" }, { b: "live" }],
      shotAlt:
        "The neplaceshop.md homepage in Romanian: an orange banner reading Accesorii auto inteligente, surrounded by dash cameras, phone mounts and car accessories, above the product search bar.",
      blocks: [
        {
          lbl: "The problem",
          h3: "Two languages, one local payment rail, hundreds of products with no copy.",
          p: [
            "The catalogue had to exist fully in Romanian and Russian, not as a half-translated afterthought. Payments had to clear through MAIB, the local bank, not Stripe. And every product needed description text that nobody had time to write.",
          ],
        },
        {
          lbl: "The approach",
          h3: "Language as a first-class dimension, not a plugin.",
          p: [
            "Content is stored per locale at the field level, so a product can be complete in one language and pending in the other without breaking the page. Routes carry the locale, so both versions are separately indexable.",
            "Product copy is generated once from the spec sheet, then edited by a human before publishing. Generated text that nobody reviews is worse than no text.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Storefront, payments, and the notifications around them.",
          p: [
            "React front end on an Express API with PostgreSQL. MAIB gateway for card payments, fiscal invoicing on order completion. Order events fan out to Telegram for the shop and email for the customer.",
            "Pages are pre-rendered so the crawler sees full HTML, with Schema.org JSON-LD per product.",
          ],
        },
        { lbl: "The outcome", h3: "1 500 visitors a day, both languages complete.", p: [] },
      ],
      chips: ["React", "Express", "PostgreSQL", "MAIB gateway", "fiscal invoicing", "OpenAI copy", "Telegram", "Mailgun", "React Snap", "JSON-LD"],
      kpis: [{ v: "1 500", l: "daily visitors" }, { v: "0.66 s", l: "largest paint" }, { v: "320", l: "routes in the sitemap" }],
      outcome:
        "Measured on the live site: the largest element paints in 0.66 s, and a product page reaches the crawler with 700 words of its own rather than an empty shell.",
    },
    {
      scene: "trading",
      slug: "trading",
      index: 4,
      phases: { steps: ["exploded", "placed", "assembled", "trading"], hint: "scroll to assemble the desk" },
      title: "AI-driven trading research bot",
      meta: [{ b: "R&D", t: " · prediction markets + crypto" }, { t: "ongoing" }, { t: "in-house" }, { b: "paper trading" }],
      blocks: [
        {
          lbl: "The question",
          h3: "Does reasoning beat indicators on long holds?",
          p: [
            "Not a product. An experiment with a falsifiable question: on positions held for days rather than seconds, does a language model reading the same data as a rules engine make better decisions than the rules engine?",
            "Run on paper, with real prices, and a hard daily loss cap.",
          ],
        },
        {
          lbl: "The approach",
          h3: "The model proposes, the risk engine decides size.",
          p: [
            "Claude reads market context and produces a thesis with a confidence number. The risk engine, which is ordinary deterministic code, turns confidence into a position size bounded by available balance and a daily loss cap. The model never sizes its own bets.",
            "Every decision is logged with its inputs, so a bad trade can be replayed and argued about later.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Scheduler, exchange, ledger, dashboard.",
          p: [
            "Python 3.12. ccxt for exchange access, pandas-ta for indicators, Anthropic Claude Opus for the reasoning step, SQLAlchemy over aiosqlite as the ledger, APScheduler driving the loop, Streamlit for the dashboard.",
          ],
        },
        { lbl: "The outcome", h3: "Promising, and still an experiment.", p: [] },
      ],
      chips: ["Python 3.12", "Claude Opus", "ccxt", "pandas-ta", "SQLAlchemy", "aiosqlite", "APScheduler", "Streamlit", "risk engine"],
      kpis: [{ v: "~+1.5%", l: "median daily P&L" }, { v: "1 wk", l: "live paper test" }],
      outcome:
        "One week on $100 of paper is a signal, not a result. It is reported here as what it is: an open experiment with its logs intact.",
    },
    {
      scene: null,
      site: { host: "createli.md", url: "https://createli.md", w: 1440, h: 5219 },
      slug: "createli",
      index: 5,
      phases: { steps: [], hint: "" },
      title: "Createli Group — custom furniture",
      meta: [{ b: "Website", t: " · furniture manufacturer" }, { t: "Romanian" }, { t: "in-house" }, { b: "live" }],
      shotAlt:
        "The createli.md homepage: a fitted kitchen photographed in daylight, with the headline Mobilier personalizat pentru Toate Nevoile and two buttons, one for wood furniture and one for school furniture.",
      blocks: [
        {
          lbl: "The problem",
          h3: "One workshop, two buyers who share nothing.",
          p: [
            "Createli builds custom wood furniture for homes and equips schools and kindergartens. A family choosing a kitchen and a procurement officer buying forty desks are not looking for the same page, and they do not read the same words. One site had to serve both without turning into a catalogue that serves neither.",
          ],
        },
        {
          lbl: "The approach",
          h3: "The split happens on the first screen.",
          p: [
            "The hero offers the two directions as two buttons, and each leads to a page written in its own language: materials, finishes and fitting for the home; durability, dimensions and institutional supply for schools.",
            "Products are data, not pages. Each item has its own route generated from the API, so adding one is a database row rather than a deploy.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "React in front, rendered to static HTML at build.",
          p: [
            "Vite and React with an Express API behind it. Every marketing route and every product page is rendered by a headless browser during the build, so a crawler or a social scraper receives complete HTML with the meta that belongs to that page — not an empty shell that only fills in after JavaScript runs.",
            "The prerender runs against a local server, not against the live site. That keeps the build self-contained: no deploy depends on a previous deploy still being up.",
          ],
        },
        { lbl: "The outcome", h3: "Thirteen routes that arrive complete.", p: [] },
      ],
      chips: ["React", "Vite", "TypeScript", "Tailwind", "shadcn/ui", "Express", "Puppeteer prerender", "react-helmet-async", "PM2", "nginx"],
      kpis: [{ v: "0.000", l: "layout shift" }, { v: "2.3 s", l: "largest paint" }, { v: "13", l: "prerendered routes" }],
      outcome:
        "Measured on the live site: nothing moves while the page loads, and the largest element paints inside Google's threshold for good.",
    },
    {
      scene: null,
      site: { host: "elegance-transfer.com", url: "https://elegance-transfer.com", w: 1440, h: 7710 },
      slug: "elegance",
      index: 6,
      phases: { steps: [], hint: "" },
      title: "Elegance Transfer — booking in four languages",
      meta: [{ b: "Website", t: " · transfer operator" }, { t: "4 languages" }, { t: "in-house" }, { b: "live" }],
      shotAlt:
        "The elegance-transfer.com homepage in Russian: a dark hero with a sedan at night, the headline Люкс и комфорт в каждой поездке, and a booking button.",
      blocks: [
        {
          lbl: "The problem",
          h3: "Six corridors sold to four language markets.",
          p: [
            "Elegance drives the Moldova–Ukraine–Romania corridor: airport runs, intercity transfers, vehicle delivery. The customers arrive in Russian, Romanian, English and Ukrainian, and someone searching for one specific route is not served by a homepage that lists all of them.",
          ],
        },
        {
          lbl: "The approach",
          h3: "Every route is its own page, in every language.",
          p: [
            "Chișinău to Odesa, Bucharest, Iași, Kyiv, Lviv and Cernăuți each have a page, and each page exists in all four languages with its own address. A search for a single corridor lands on the page for that corridor.",
            "The booking form writes to the database and pushes the request into Telegram, where the dispatcher already works. No second tool to open, no dashboard to learn.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Four locales at the field level, Telegram as the console.",
          p: [
            "Vite and React with i18next carrying the four languages field by field, so a page can be complete in one and pending in another without breaking. Express and MySQL behind it, and a Telegram bot that receives every booking.",
            "After each deploy a headless browser renders all 248 routes to static HTML, so every route page and every vehicle reaches the crawler with its own meta and JSON-LD instead of an empty shell.",
          ],
        },
        { lbl: "The outcome", h3: "248 routes, four languages, all indexable.", p: [] },
      ],
      chips: ["React", "Vite", "TypeScript", "Tailwind", "i18next", "Express", "MySQL", "Telegram Bot API", "Puppeteer prerender", "JSON-LD"],
      kpis: [{ v: "1.3 s", l: "largest paint" }, { v: "248", l: "prerendered routes" }, { v: "4", l: "languages" }],
      outcome:
        "Measured on the live site: 1.3 s to the largest paint and a layout shift of 0.001, with each of the four languages separately indexable.",
    },
    {
      scene: null,
      site: { host: "actexpress.md", url: "https://actexpress.md", w: 1440, h: 4841 },
      slug: "actexpress",
      index: 7,
      phases: { steps: [], hint: "" },
      title: "ActExpress — Romanian documents agency",
      meta: [{ b: "Website", t: " · documents agency" }, { t: "2 languages" }, { t: "in-house" }, { b: "live" }],
      shotAlt:
        "The actexpress.md homepage in Romanian: a passport and papers photographed on a desk, the headline Partenerul Tău de Încredere pentru Documente Românești, and a short contact form beside it.",
      blocks: [
        {
          lbl: "The problem",
          h3: "Seven procedures, two languages, one form.",
          p: [
            "ActExpress handles Romanian citizenship, passports, identity cards, driving licences, criminal record certificates, apostille and certified translations. Seven procedures, each with its own paperwork, sold to clients who read Romanian or Russian. The site had to explain all seven clearly enough that someone recognises their own case, then capture the enquiry with the one detail that decides everything that follows: which procedure.",
          ],
        },
        {
          lbl: "The approach",
          h3: "The service is a field, not a sentence.",
          p: [
            "The enquiry form carries the procedure as its own field, so a request arrives already sorted instead of as free text somebody has to read and classify before it can be answered.",
            "Both languages are in the interface from the first load, detected from the browser and switchable by hand. The privacy policy and terms exist in both as well — an agency that handles other people's documents and cannot show its own terms is a hard sell.",
          ],
        },
        {
          lbl: "The architecture",
          h3: "Validated on both sides, delivered to two places.",
          p: [
            "React on Vite in front, Express and MySQL behind. The form validates in the browser with react-hook-form and again on the server with express-validator, because a check that runs in the browser is a convenience, not a guarantee.",
            "Every enquiry is written to the database and emailed to the office in the same request, with rate limiting in front of the endpoint so the form cannot be turned into a mail relay.",
          ],
        },
        { lbl: "The outcome", h3: "The lightest of the four sites here.", p: [] },
      ],
      chips: ["React", "Vite", "react-i18next", "react-hook-form", "Tailwind", "Sass", "Express", "MySQL", "Nodemailer", "express-validator"],
      kpis: [{ v: "674 KB", l: "page weight" }, { v: "1.6 s", l: "largest paint" }, { v: "22", l: "requests" }],
      outcome:
        "Measured on the live site: 674 KB over 22 requests, less than half the weight of the other three, with the largest element painting in 1.6 s.",
    },
  ],

  notFound: {
    eyebrow: "Error · 404",
    title: ["That route", "doesn’t compile."],
    text: (p) => `${p} doesn’t match any registered route.`,
    notes: ["the studio in one page", "seven projects, and what each changed", "what we build, in seven lines", "price and timeline, answered", "two questions and a description"],
  },

  footer: {
    rights: "© 2026 · Independent engineering studio",
    reach: "Chișinău, Moldova — working across Moldova and Romania",
    privacy: "Privacy",
    terms: "Terms",
  },

  legalOnlyEn: "This document is available in English only.",
};
